/**
 * @file ProductKindList.tsx
 * @description Колонки справочника «Виды товара» для `ResponsiveList`
 * (phase19.22 W22; phase19.31 — каркас справочника (б) разбора B).
 *
 * От 1024 — таблица: Название · Срок · Этикетка · Регламент · НДС · Возврат ·
 * Товаров; до 1024 — компактный список: название и число товаров, второй
 * линией — сводка вида (`productKindSummary`). Архивный вид — серая пилюля
 * «В архиве» рядом с названием. Действия — в «⋯» строки (набор собирает
 * `useProductKindActions`).
 */

import { StatusPill } from "../../ui/StatusPill";
import type { ListColumn } from "../../ui/responsive-list-types";
import type { ProductKind } from "../../../lib/api-product-kinds";
import { LABEL_TEMPLATE_META, productKindSummary, regulationLabel, shelfLifeMonthsHint } from "../../../lib/product-kind-format";

/** Колонка «Срок»: «не нужен» / «да, без умолчания» / «365 дн. · ≈ 12 мес.». */
function expiryCell(kind: ProductKind): string {
  if (!kind.requiresExpiry) return "не нужен";
  if (!kind.defaultShelfLifeDays) return "да, без умолчания";
  return `${kind.defaultShelfLifeDays} дн. · ${shelfLifeMonthsHint(kind.defaultShelfLifeDays)}`;
}

function nameCell(kind: ProductKind) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate font-medium">{kind.name}</span>
      {!kind.isActive ? <StatusPill tone="neutral">В архиве</StatusPill> : null}
    </span>
  );
}

export const PRODUCT_KIND_COLUMNS: ListColumn<ProductKind>[] = [
  { key: "name", header: "Название", cell: nameCell, compact: "title" },
  { key: "expiry", header: "Срок", width: "w-[168px]", cell: (k) => <span className="tabular-nums">{expiryCell(k)}</span> },
  { key: "label", header: "Этикетка", width: "w-[132px]", cell: (k) => LABEL_TEMPLATE_META[k.labelTemplate].label },
  { key: "regulation", header: "Регламент", width: "w-[148px]", cell: (k) => regulationLabel(k), fold: { into: "label" } },
  {
    key: "vat",
    header: "НДС",
    width: "w-[96px]",
    cell: (k) => <span className="tabular-nums">{k.defaultVatRate === null ? "вручную" : `${k.defaultVatRate} %`}</span>,
  },
  { key: "return", header: "Возврат", width: "w-[88px]", cell: (k) => (k.returnToStockAllowed ? "да" : "нет"), fold: { into: "vat", cell: (k) => `возврат: ${k.returnToStockAllowed ? "да" : "нет"}` } },
  {
    key: "products",
    header: "Товаров",
    width: "w-[96px]",
    align: "end",
    cell: (k) => <span className="tabular-nums">{k.productsCount}</span>,
    compact: "trailing",
    compactCell: (k) => <span className="tabular-nums">{k.productsCount} тов.</span>,
  },
  { key: "summary", header: "Сводка", compactOnly: true, cell: (k) => productKindSummary(k).join(" · "), compact: "subtitle" },
];
